'use client';
import axios from 'axios';
import moment from 'moment';
import { Fragment, useEffect, useState, useRef } from 'react';
import classes from './FindAddressFromZip.module.css';
import ProductPageButtons from '@/app/product/[id]/[title]/components/ProductPageButtons';
import useLocalStorage from '../Hooks/UseLocalStorage';
import { useAuthContext } from '@/context';
import GetDistanceBetween from '@/utils/GetDistanceBetween';
import fedExEstimateHandler from '@/utils/fedExEstimateHandler';
import goShareEstimatHandler from '@/utils/goShareEstimatHandler';
import uShipEstimateHandler from '@/utils/uShipEstimateHandler';
import { saveUserAddress } from '@/utils/SaveUserAddress';
import HowFedExWorksModal from '@/components/modals/delivery-related/HowFedExWorksModal';
import HowGoShareWorksModal from '@/components/modals/delivery-related/HowGoShareWorksModal';
import HowNationWideWorksModal from '@/components/modals/delivery-related/HowNationWideWorksModal';
import Image from 'next/image';
import { useListingsContext } from '@/context/listings';
import getLocation from '@/utils/getLocation';
import { LoaderCircle } from 'lucide-react';
import { MapPin, X } from 'react-feather';

const FindAddressFromZip = (props) => {
	const { listing, author, deviceWidth } = props;

	const { LOAD_LISTINGS } = useListingsContext();

	const [cheapestDeliveryOption, setCheapestDeliveryOption] = useState();

	const [fedExEstimateObj, setFedExEstimateObj] = useState();
	const [goShareEstimate, setGoShareEstimate] = useState();
	const [error, setError] = useState();
	const [fedExEstimate, setFedExEstimate] = useState();
	const [uShipDeliveryEstimate, setuShipDeliveryEstimate] = useState();
	const [addressState, setAddress] = useState();
	const [offered, setOffered] = useState();

	const [loadingGS, setLoadingGS] = useState(false);
	const [loadingUship, setLoadingUship] = useState(false);
	const [loadingFedEx, setLoadingFedEx] = useState(false);
	const [uShipReqEst, setUshipReqEst] = useState();
	const [goShareModal, setGoShareModal] = useState(false);
	const [nationWideModal, setNationWideModal] = useState(false);
	const [fedExModal, setFedExModal] = useState(false);
	const [goShareReqEst, setGoShareReqEst] = useState();
	const [goShareReqBody, setGoShareReqBody] = useState();

	const [showAddressModal, setShowAddressModal] = useState(false);
	const [defaultAddress, setDefaultAddress] = useLocalStorage(
		'defaultAddress',
		[]
	);

	const [windowWidth, setWindowWidth] = useState(deviceWidth ? deviceWidth : 0);

	useEffect(() => {
		setWindowWidth(window.innerWidth);
	}, []);

	useEffect(() => {
		const ushipDate = moment().add(2, 'weeks').format('dddd, MMM Do');
		const fedExDate = moment().add(4, 'days').format('dddd, MMM Do');
		setCheapestDeliveryOption();
		let options = [
			{
				method: 1,
				name: 'GoShare',
				price: goShareEstimate,
				asSoonAs: 'Today',
				deliveryTime: 'Local on demand delivery',
				loading: loadingGS,
				priority: 1, // Highest priority
				modal: setGoShareModal,
			},
			{
				method: 2,
				name: 'Seller',
				price: offered,
				asSoonAs: 'Tomorrow',
				deliveryTime: 'Local on demand delivery',
				loading: loadingGS, // Assuming this is a typo and should be another loading state
				priority: 2, // Set to 2 to ensure lower priority than GoShare if prices are same
				modal: null,
			},
			{
				method: 4,
				name: 'uShip',
				price: uShipDeliveryEstimate,
				asSoonAs: ushipDate,
				deliveryTime: '2-4 week delivery',
				loading: loadingUship,
				priority: 3,
				modal: setNationWideModal,
			},
			{
				method: 5,
				name: 'FedEx',
				price: fedExEstimate,
				asSoonAs: fedExDate,
				deliveryTime: '1-5 day delivery',
				loading: loadingFedEx,
				priority: 4,
				modal: setFedExModal,
			},
		];

		const validOptions = options?.filter(
			(option) => option.price && !option.loading
		);

		if (validOptions?.length > 0) {
			const goShareOption = validOptions.find(
				(option) => option.name === 'GoShare'
			);
			const sellerOption = validOptions.find(
				(option) => option.name === 'Seller'
			);

			let preferredOption = null;
			if (goShareOption && sellerOption) {
				// If both GoShare and Seller options are available, compare and choose the cheaper
				preferredOption =
					goShareOption.price <= sellerOption.price
						? goShareOption
						: sellerOption;
			} else if (goShareOption) {
				preferredOption = goShareOption; // Only GoShare is available
			}

			const cheapestOption =
				preferredOption ||
				validOptions.reduce((prev, current) =>
					prev.price < current.price ||
					(prev.price === current.price && prev.priority < current.priority)
						? prev
						: current
				);

			setCheapestDeliveryOption(cheapestOption);
		}
	}, [
		goShareEstimate,
		offered,
		loadingGS,
		loadingUship,
		loadingFedEx,
		uShipDeliveryEstimate,
		fedExEstimate,
	]);

	const calculateAndSetDistance = async (coordinates1) => {
		if (coordinates1.length > 0) {
			const distance = await GetDistanceBetween(
				coordinates1[0],
				coordinates1[1],
				listing.delivery.coordinates[0],
				listing.delivery.coordinates[1]
			)
				.then((response) => {
					return response;
				})
				.catch((error) => console.log(error));
			return distance;
		}
	};

	const { auth } = useAuthContext();

	const checkSellerDelivery = (distance) => {
		if (listing.freeShipping) {
			setOffered(0.0001);
		} else if (listing.fixedPrices?.shipping >= 0) {
			setOffered(listing.fixedPrices?.shipping);
		} else if (
			distance &&
			listing.store?.delivery?.offered &&
			listing.store?.delivery?.deliveryRadiuses?.length > 0
		) {
			let amount = 10000000000;
			for (let r of listing.store.delivery.deliveryRadiuses) {
				if (distance <= parseInt(r.miles) && r.fee < amount) {
					amount = r.fee;
				}
			}
			if (amount < 10000000000) {
				setOffered(parseInt(amount) > 0 ? parseInt(amount) : 0.0001);
			}
		} else {
			setOffered(null);
		}
	};

	const onSelectedAddress = async (coordinates, address, selectedLocation) => {
		setGoShareEstimate();
		setuShipDeliveryEstimate();
		setFedExEstimate();
		setAddress(address);

		if (!coordinates && !address && !selectedLocation) {
			setOffered(null);
			setLoadingUship(false);
			setLoadingGS(false);
			setLoadingFedEx(false);
			return;
		}
		// find distance between pickup and drop location
		const distance = await calculateAndSetDistance(coordinates);
		if (auth.isAuthenticated && !auth.user.locationInfo?.location) {
			let locationInfo = {
				location: selectedLocation,
				coordinates,
				address,
				stairs: false,
				elevator: false,
				flights: 0,
				otherInfo: '',
			};
			saveUserAddress(locationInfo);
		}

		await Promise.all([
			getGoShareEstimate(coordinates, address, selectedLocation, distance),
			getUShipEstimate(coordinates, address, selectedLocation),
			getFedExEstimate(selectedLocation),
			checkSellerDelivery(distance),
		]);
	};

	const getGoShareEstimate = async (
		coordinates1,
		address1,
		selectedLocation,
		distance
	) => {
		if (
			(!listing.store ||
				listing?.store?.delivery?.deliveryProviders?.goShare) &&
			distance <= 150
		) {
			setLoadingGS(true);
			await goShareEstimatHandler({
				batch: {
					listings: [{ listing, quantity: 1, listingPrice: listing.price }],
				},
				coordinates: coordinates1,
				address: address1,
				location: selectedLocation,
				buyer: auth.user,
				seller: author,
			})
				.then((response) => {
					setGoShareReqEst(response);
					setGoShareReqBody(JSON.parse(response.body));
					setGoShareEstimate(
						response?.totalPrice ? response.totalPrice / 100 : null
					);
				})
				.catch((err) => {
					console.log(err);
				})
				.finally(() => {
					setLoadingGS(false);
				});
		}
	};

	const getUShipEstimate = async (coordinates1, address1, selectedLocation) => {
		if (!listing.store || listing?.store?.delivery?.deliveryProviders?.uShip) {
			setLoadingUship(true);
			const response3 = await uShipEstimateHandler({
				batch: {
					listings: [
						{
							listing,
							quantity: 1,
							listingPrice: listing.price,
							store: listing.store,
						},
					],
					author,
				},
				coordinates: coordinates1,
				address: address1,
				location: selectedLocation,
				buyer: auth.user,
				seller: author,
			});

			setuShipDeliveryEstimate(response3?.priceDetails?.price);
			setUshipReqEst(response3);
			setLoadingUship(false);
		}
	};

	const getFedExEstimate = async (selectedLocation) => {
		setLoadingFedEx(true);
		const response = await fedExEstimateHandler({
			batch: {
				listings: [
					{
						listing,
						quantity: 1,
						listingPrice: listing.price,
						store: listing.store,
					},
				],
				author,
			},
			location: selectedLocation,
		});
		if (response) {
			let totalNetChargeObject =
				response.output.rateReplyDetails[
					response.output.rateReplyDetails.length - 1
				];
			let totalNetChargePrice =
				totalNetChargeObject.ratedShipmentDetails[0].totalNetCharge;
			setFedExEstimate(totalNetChargePrice);
			setFedExEstimateObj(totalNetChargeObject);
		}
		setLoadingFedEx(false);
	};

	const [showResults, setShowResults] = useState(false);
	const [data, setData] = useState([]);
	const [searchText, setSearchText] = useState('');
	const [displayPostalCode, setDisplayPostalCode] = useState();
	const [run, setRun] = useState(0);
	useEffect(() => {
		const isBot = /bot|crawl|slurp|spider|Prerender|facebookexternalhit/i.test(
			navigator?.userAgent
		);

		const getLoc = async () => {
			if (
				localStorage.getItem('lat') &&
				localStorage.getItem('long') &&
				localStorage.getItem('postalCode') &&
				localStorage.getItem('postalCode') !== 'undefined'
			) {
				setDisplayPostalCode(localStorage.getItem('postalCode'));
				return;
			} else if (run < 4) {
				setRun(run + 1);
				await axios
					.get(
						`${process.env.NEXT_PUBLIC_SERVER_API}/api/other/location-ipInfo`
					)
					.then((response) => {
						const data = response.data?.data;
						setDisplayPostalCode(data.postal);
						const [latitude, longitude] = data.loc?.split(',');

						localStorage.setItem('lat', latitude);
						localStorage.setItem('long', longitude);
						localStorage.setItem('postalCode', data.postal);

						let region = getLocation(latitude, longitude);

						if (region) {
							localStorage.setItem('region', region);

							const params = new URLSearchParams(window.location?.search);

							LOAD_LISTINGS({
								page: params?.get('page') || 1,
								locationSearch: params,
								userToken: auth?.user?._id,
							});
						}
					})
					.catch((error) => {
						console.log(error);
					});
			}
		};

		if (!isBot) {
			getLoc();
		}
	}, []);

	useEffect(() => {
		const isBot = /bot|crawl|slurp|spider|Prerender|facebookexternalhit/i.test(
			navigator?.userAgent
		);
		if (!isBot && searchText) {
			const fetchZipCodes = async () => {
				try {
					const response = await axios.get(
						`https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURI(
							searchText
						)}.json`,
						{
							params: {
								country: 'US',
								types: 'postcode',
								access_token: process.env.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN,
							},
						}
					);
					const locations = response.data.features.map((feature) => {
						// Assuming the context array's length and order are consistent.
						// Usually, the context array would contain details like city, district, and state.
						// Here's a more robust way to handle it:
						const city = feature.context.find((c) =>
							c.id.startsWith('place.')
						)?.text;
						const state = feature.context.find((c) =>
							c.id.startsWith('region.')
						)?.text;
						const zip = feature.text; // Postal code is usually the main text for type "postcode"

						return {
							zip: zip,
							state: state,
							city: city,
						};
					});

					setData(locations);
				} catch (error) {
					if (setError) setError(`Failed to fetch ZIP codes: ${error.message}`);
				}
			};

			fetchZipCodes();
		}
	}, [searchText]); // Include accessToken in the dependency array if it's expected to change over time

	const inputRef = useRef();

	const placeSelectedHandler = (location) => {
		if (error && error === 'Select an address first' && setError) {
			setError();
		}
		if (location) {
			// process.env.NEXT_PUBLIC_NODE_ENV === 'production' &&
			// process.env.OG_DB === 'true' &&
			// 	global?.window &&
			// 	window.analytics.track('address_selected', {
			// 		address: location['place_name'],
			// 	});

			onSelectedAddress(location.center, location.place_name, location);
		}
	};

	const getClosestAddressToPostalCode = async (postalCode) => {
		if (
			global?.window &&
			localStorage.getItem('defaultAddress') &&
			localStorage.getItem('defaultAddress') !== 'undefined' &&
			JSON.parse(localStorage.getItem('defaultAddress'))?.context?.find((i) =>
				i.id.includes('postcode')
			)?.text === postalCode.replace(/"/g, '')
		) {
			placeSelectedHandler(JSON.parse(localStorage.getItem('defaultAddress')));
			setDisplayPostalCode(postalCode);
			setDefaultAddress(JSON.parse(localStorage.getItem('defaultAddress')));
			setShowResults(false);
			setSearchText(postalCode.replace(/"/g, ''));
			setShowAddressModal(false);
		} else {
			await axios
				.post(`${process.env.NEXT_PUBLIC_SERVER_API}/api/other/get-address`, {
					zip: postalCode,
				})
				.then((res) => {
					// localStorage.setItem('defaultAddress', JSON.stringify(res.data));
					if (res.data) {
						localStorage.removeItem('defaultAddress');
						localStorage.setItem('lat', res.data.center[1]);
						localStorage.setItem('long', res.data.center[0]);
						localStorage.setItem('postalCode', postalCode.replace(/"/g, ''));

						let region = getLocation(res.data.center[1], res.data.center[0]);

						if (region) {
							localStorage.setItem('region', region);
							const params = new URLSearchParams(window.location?.search);

							LOAD_LISTINGS({
								page: params?.get('page') || 1,
								locationSearch: params,
								userToken: auth?.user?._id,
							});
						}
						placeSelectedHandler(res.data);
						setDisplayPostalCode(postalCode);
						// setDefaultAddress(res.data);
						setShowResults(false);
						setSearchText(postalCode.replace(/"/g, ''));
						setShowAddressModal(false);
					}
				})
				.catch((err) => {
					console.log(err);
				});
		}
	};

	useEffect(() => {
		if (
			global?.window &&
			defaultAddress &&
			localStorage.getItem('defaultAddress') !== '' &&
			defaultAddress !== null &&
			defaultAddress['place_name']
		) {
			const defaultAddress = JSON.parse(localStorage.getItem('defaultAddress'));
			placeSelectedHandler(defaultAddress);
		} else if (displayPostalCode) {
			getClosestAddressToPostalCode(displayPostalCode);
		} else {
			return;
		}
	}, [listing, defaultAddress, displayPostalCode]);

	const extractZipCode = (address) => {
		// Regular expression pattern to match ZIP code
		const zipCodePattern = /\b\d{5}(?:-\d{4})?\b/;

		// Extract ZIP code from the address
		const zipCodeMatch = address.match(zipCodePattern);

		// Check if a ZIP code was found and return it
		return zipCodeMatch ? zipCodeMatch[0] : '';
	};

	function extractCityState(address) {
		const parts = address?.split(','); // Split the address by comma
		if (parts?.length >= 3) {
			const city = parts[parts.length - 3].trim(); // City is the second last part
			const zip = extractZipCode(address); // Extract ZIP code from the address
			return `${city}, ${zip}`;
		} else {
			return null;
		}
	}

	const handleKeyPress = (e) => {
		if (e.key === 'Enter' && data.length > 0) {
			e.preventDefault();
			const topAddress = data[0];
			getClosestAddressToPostalCode(topAddress.zip);
			setShowResults(false); // Hide the results after selection
		}
	};

	useEffect(() => {
		const checkIfClickedOutside = (e) => {
			// Check if there's a click outside of the input and result list
			if (
				showResults &&
				showAddressModal &&
				inputRef.current &&
				!inputRef.current.contains(e.target)
			) {
				setShowResults(false);
				setShowAddressModal(false);
				if (data.length > 0) {
					const topAddress = data[0];
					getClosestAddressToPostalCode(topAddress.zip);
				}
			}
		};

		document.addEventListener('mousedown', checkIfClickedOutside);

		return () => {
			document.removeEventListener('mousedown', checkIfClickedOutside);
		};
	}, [
		searchText,
		showResults,
		data,
		showAddressModal,
		displayPostalCode,
		cheapestDeliveryOption,
	]);

	const [discountPrice, setDiscountPrice] = useState();
	useEffect(() => {
		if (
			props.shippingDiscounts &&
			cheapestDeliveryOption?.price &&
			cheapestDeliveryOption?.price > 1
		) {
			let newDiscountPrice = cheapestDeliveryOption.price;
			for (let discount of props.shippingDiscounts) {
				if (discount.discountType === 'percentage') {
					newDiscountPrice =
						newDiscountPrice - (newDiscountPrice * discount.amount) / 100;
				} else if (discount.discountType === 'amount') {
					newDiscountPrice = newDiscountPrice - discount.amount;
				}
			}

			if (newDiscountPrice < cheapestDeliveryOption.price) {
				setDiscountPrice(newDiscountPrice);
			}
		}
	}, [cheapestDeliveryOption, props.shippingDiscounts]);

	return (
		<Fragment>
			<div className={classes.main}>
				<section className={classes.box}>
					<section className={classes.justifyBetween}>
						<section
							className={classes.gap}
							onClick={() => setShowAddressModal(!showAddressModal)}
						>
							<Image
								width={25}
								height={25}
								src="https://ik.imagekit.io/kuj9zqlrx0or/Static/In-Transit.png"
								alt="Delivery offered kashew, buy used furniture"
								className={classes.deliveryIcon}
							/>
							<section className={classes.col}>
								<h5 className={classes.text1}>
									{cheapestDeliveryOption &&
									cheapestDeliveryOption.name === 'FedEx'
										? 'FedEx Ground delivery to '
										: 'White glove delivery to '}
									<strong className={classes.strong2}>
										{displayPostalCode &&
										displayPostalCode.startsWith('"') &&
										displayPostalCode.endsWith('"')
											? displayPostalCode.slice(1, -1)
											: displayPostalCode}{' '}
									</strong>
								</h5>
								<h5 className={classes.text2}>
									As soon as{' '}
									{cheapestDeliveryOption?.asSoonAs ? (
										cheapestDeliveryOption?.asSoonAs
									) : (
										<div className={classes.loadingPlaceholder} />
									)}
								</h5>
							</section>
						</section>
						<h5 className={classes.text1}>
							<section className={classes.col1}>
								{cheapestDeliveryOption && !cheapestDeliveryOption.loading ? (
									<strong
										className={
											cheapestDeliveryOption.price.toFixed() < 0.05
												? classes.free
												: classes.strong
										}
									>
										{cheapestDeliveryOption.price.toFixed() < 0.05 ? (
											`Free ${
												windowWidth > 600 || deviceWidth > 600 ? 'Delivery' : ''
											} 🚚`
										) : discountPrice ? (
											<>
												{' '}
												${discountPrice?.toFixed(0)}{' '}
												<span
													style={{
														textDecoration: 'line-through',
														fontWeight: 300,
													}}
												>
													${cheapestDeliveryOption.price.toFixed()}
												</span>
											</>
										) : (
											`$${cheapestDeliveryOption.price.toFixed()}`
										)}
									</strong>
								) : (
									<div className={classes.loadingSpinner}>
										<LoaderCircle className={classes.spinner} size={15} />
									</div>
								)}
								{cheapestDeliveryOption?.modal && (
									<h5
										className={classes.text3}
										onClick={
											cheapestDeliveryOption?.modal
												? cheapestDeliveryOption.modal
												: () => {}
										}
									>
										How it works{' '}
									</h5>
								)}
							</section>
						</h5>
					</section>
				</section>
				{showAddressModal && (
					<section className={classes.absolute}>
						<div className={classes.close}>
							<X
								className={classes.closeIcon}
								onClick={() => setShowAddressModal(false)}
								size={15}
							/>
						</div>
						<h5 className={classes.text1}>
							Enter the delivery postal code to estimate the price
						</h5>
						<section className={classes.relative} ref={inputRef}>
							<input
								className={classes.inputVal}
								value={searchText}
								placeholder="Enter your zip code"
								onClick={() => setShowResults(true)}
								onChange={(e) => setSearchText(e.target.value)}
								onKeyPress={(e) => handleKeyPress(e)}
							/>
							{searchText && showResults ? (
								data.length > 0 ? (
									<ul className={classes.overflow}>
										{data.map((spot, idx) => {
											return (
												<li
													className={
														idx === 0
															? classes.resultsListTop
															: classes.resultsList
													}
													key={idx}
													onClick={(event) => {
														event.preventDefault();
														getClosestAddressToPostalCode(spot.zip);
													}}
												>
													<MapPin className={classes.locationIcon} size={15} />
													<h6 className={classes.text1}>
														{spot.zip}, {spot.city}, {spot.state}
													</h6>
												</li>
											);
										})}
									</ul>
								) : (
									<>
										{' '}
										<div className={classes.overflow}>
											<div variant="success" className={classes.resultsList2}>
												{searchText.length < 2 && data.length < 1 ? (
													<h6 className={classes.text5}>Continue typing...</h6>
												) : (
													<h6 className={classes.text5}>
														No results found. Please enter your postal code and
														select from the list.
													</h6>
												)}
											</div>
										</div>
									</>
								)
							) : (
								''
							)}
						</section>
					</section>
				)}

				<section className={classes.box}>
					<section className={classes.justifyBetween}>
						<section className={classes.gap}>
							<Image
								width={25}
								height={25}
								src="https://ik.imagekit.io/kuj9zqlrx0or/Static/Shop-Location.png"
								alt="on Demand white glove delivery for used furniture offered kashew, buy used furniture"
								className={classes.deliveryIcon}
							/>{' '}
							<section className={classes.col}>
								<h5 className={classes.text1}>
									Pickup in-store from{' '}
									{extractCityState(listing?.delivery?.address)}
								</h5>
								<h5 className={classes.text2}>As soon as Today</h5>
							</section>
						</section>
						<h5 className={classes.text1}>
							<strong className={classes.strong}>Free</strong>
						</h5>
					</section>
				</section>
			</div>

			<HowGoShareWorksModal
				setGoShareModal={setGoShareModal}
				goShareModal={goShareModal}
			/>
			<HowNationWideWorksModal
				setNationWideModal={setNationWideModal}
				nationWideModal={nationWideModal}
			/>

			<HowFedExWorksModal
				setFedExModal={setFedExModal}
				fedExModal={fedExModal}
			/>

			<ProductPageButtons
				listing={listing}
				author={author}
				addressState={addressState}
				goShareEstimate={goShareEstimate}
				offered={offered}
				uShipDeliveryEstimate={uShipDeliveryEstimate}
				fedExEstimate={fedExEstimate}
				goShareReqBody={goShareReqBody}
				goShareReqEst={goShareReqEst}
				uShipReqEst={uShipReqEst}
				fedExEstimateObj={fedExEstimateObj}
			/>
		</Fragment>
	);
};

export default FindAddressFromZip;
